/**
 * The Charter audio bridge (M5 spec §20; plan C19): strategic views → the Charter's music cues. Pure state, no Web
 * Audio: the feature (src/features/charterAudio/feature.ts) feeds it the client's views and plays what it returns.
 *
 * The strategic events reach the main thread as views, so the bridge watches for changes (the first view of each
 * kind is the baseline and plays nothing):
 * - **Claim flip:** the watched system's `claim.holder` changes (same system; entering another system is no flip).
 * - **War declared / peace:** a faction pair's relation becomes `war`, or leaves it (`RelationsView.pairs`).
 * - **Gate:** normal → flickers is the first flicker (subtle); → tremors a tremor and → closed the closure (both
 *   BRAAAM-scale); leaving `closed` the reopening.
 * - **Vote result:** the tetherstone ban comes into force or is repealed (`SystemEconomyView.tetherstoneIllegal`).
 *   A vote that fails changes no view, so it plays nothing (D-TBD in the report: needs an M5 Wire kind).
 * - **Themes:** the Isolated Era theme while `GateView.isolation`; else the Descendants motif in a Holdfast system
 *   (Holdfast holds the claim, or owns most of its stations), or near a guardian ship from story tier 3 (the gate view
 *   carries `stability` only from tier 3).
 *
 * **Rate limiting** (the conductor's hysteresis, so stingers never stack): a cue repeats only after its
 * `cooldownSeconds`; one stinger waits at a time, and a higher priority replaces a lower one; stingers play at least
 * `minGapSeconds` apart. Requests and takes allocate nothing; the view observers loop over the views' own arrays.
 */
import { FACTION, FACTION_IDS, GATE_STAGE, PLAYER_FACTION_ID, RELATION, type CorpFactionId, type GateView, type RelationsView, type SystemEconomyView } from '../../strategic/types'
import { CHARTER_CUES, CUE, THEME, charterMusic, type CharterMusicConfig, type ThemeCode } from './cues'

const F = FACTION_IDS.length

export interface CharterBridgeStats {
  requested: number
  played: number
  /** Requests dropped by a cooldown or a higher-priority pending stinger. */
  dropped: number
}

export interface CharterBridge {
  /** The waiting stinger (CUE code), or -1. */
  pending: number
  /** When the last stinger played (s), and when each cue was last accepted. */
  lastPlayAt: number
  readonly lastCueAt: Float64Array
  /** The watched system's id and claim holder (null before the first view). */
  systemId: string | null
  holder: CorpFactionId | null
  holdfastSystem: boolean
  /** Tetherstone ban: -1 unknown, 0 legal, 1 banned. */
  ban: number
  /** GATE_STAGE code, -1 before the first gate view. */
  gateStage: number
  isolation: boolean
  /** Story tier 3 or later (the gate view carries the stability). */
  tier3: boolean
  /** RELATION code per ordered faction pair (a × F + b), -1 unknown. */
  readonly relations: Int8Array
  /** A guardian ship is near the player (the feature's scan). */
  guardianNear: boolean
  readonly stats: CharterBridgeStats
}

export function createCharterBridge(): CharterBridge {
  return {
    pending: -1,
    lastPlayAt: -Infinity,
    lastCueAt: new Float64Array(CHARTER_CUES.length).fill(-Infinity),
    systemId: null,
    holder: null,
    holdfastSystem: false,
    ban: -1,
    gateStage: -1,
    isolation: false,
    tier3: false,
    relations: new Int8Array(F * F).fill(-1),
    guardianNear: false,
    stats: { requested: 0, played: 0, dropped: 0 },
  }
}

/** Asks for a stinger at `now` (s). Returns whether it is now the pending one. Allocation-free. */
export function requestCue(b: CharterBridge, cue: number, now: number, cfg: CharterMusicConfig = charterMusic): boolean {
  const cues = cfg.stingers.cues
  const def = cues[CHARTER_CUES[cue]]
  b.stats.requested++
  if (now - b.lastCueAt[cue] < def.cooldownSeconds) {
    b.stats.dropped++
    return false
  }
  if (b.pending >= 0 && cues[CHARTER_CUES[b.pending]].priority >= def.priority) {
    b.stats.dropped++
    return false
  }
  if (b.pending >= 0) b.stats.dropped++
  b.pending = cue
  b.lastCueAt[cue] = now
  return true
}

/** The stinger to play now (CUE code), or -1 while none waits or inside the minimum gap. Allocation-free. */
export function takeStinger(b: CharterBridge, now: number, cfg: CharterMusicConfig = charterMusic): number {
  const cue = b.pending
  if (cue < 0 || now - b.lastPlayAt < cfg.stingers.minGapSeconds) return -1
  b.pending = -1
  b.lastPlayAt = now
  b.stats.played++
  return cue
}

/** Drops the waiting stinger (the audio is not running: it would play late). */
export function clearPending(b: CharterBridge): void {
  b.pending = -1
}

type SystemViewLike = Pick<SystemEconomyView, 'systemId' | 'tetherstoneIllegal' | 'stations'> & { readonly claim: Pick<SystemEconomyView['claim'], 'holder'> }

/** A new view of the watched system: claim flips, the ban vote and the Holdfast check. */
export function observeSystemView(b: CharterBridge, view: SystemViewLike, now: number, cfg: CharterMusicConfig = charterMusic): void {
  const holder = view.claim.holder
  if (b.systemId === view.systemId && holder !== b.holder) requestCue(b, CUE.claimFlip, now, cfg)
  b.systemId = view.systemId
  b.holder = holder
  let holdfast = 0
  for (const s of view.stations) if (s.owner === 'holdfast') holdfast++
  b.holdfastSystem = holder === 'holdfast' || (view.stations.length > 0 && holdfast * 2 > view.stations.length)
  const ban = view.tetherstoneIllegal ? 1 : 0
  if (b.ban >= 0 && ban !== b.ban) requestCue(b, CUE.voteResult, now, cfg)
  b.ban = ban
}

/** A new relations view: war declared and peace. */
export function observeRelations(b: CharterBridge, view: Pick<RelationsView, 'pairs'>, now: number, cfg: CharterMusicConfig = charterMusic): void {
  let war = false
  let peace = false
  for (const p of view.pairs) {
    if (p.a === PLAYER_FACTION_ID || p.b === PLAYER_FACTION_ID) continue // M6 player-corporation pairs carry no Charter cue
    const ia = FACTION[p.a]
    const ib = FACTION[p.b]
    if (ia === undefined || ib === undefined) continue
    const k = ia * F + ib
    const next = RELATION[p.relation]
    const prev = b.relations[k]
    if (prev >= 0 && prev !== next) {
      if (next === RELATION.war) war = true
      else if (prev === RELATION.war) peace = true
    }
    b.relations[k] = next
  }
  // War outranks peace in the same view (priorities in music.json); both are requested so the cooldowns apply.
  if (peace) requestCue(b, CUE.peace, now, cfg)
  if (war) requestCue(b, CUE.warDeclared, now, cfg)
}

/** A new gate view: the stage stingers, the Isolation theme and the story-tier-3 flag. */
export function observeGate(b: CharterBridge, view: GateView, now: number, cfg: CharterMusicConfig = charterMusic): void {
  const next: number = GATE_STAGE[view.stage]
  const prev = b.gateStage
  if (prev >= 0 && next !== prev) {
    if (prev === GATE_STAGE.closed) requestCue(b, CUE.gateReopen, now, cfg)
    else if (next === GATE_STAGE.closed) requestCue(b, CUE.gateClosure, now, cfg)
    else if (next === GATE_STAGE.tremors) requestCue(b, CUE.gateTremor, now, cfg)
    else if (next === GATE_STAGE.flickers && prev === GATE_STAGE.normal) requestCue(b, CUE.gateFlicker, now, cfg)
  }
  b.gateStage = next
  b.isolation = view.isolation
  b.tier3 = view.stability !== null
}

/** The theme that should play now. Isolation wins over the Descendants. */
export function themeOf(b: CharterBridge): ThemeCode {
  if (b.isolation) return THEME.isolated
  if (b.holdfastSystem || (b.tier3 && b.guardianNear)) return THEME.descendants
  return THEME.none
}

/** Whether the pending or last-played stinger queues the score's BRAAAM. */
export function cueBraaam(cue: number, cfg: CharterMusicConfig = charterMusic): boolean {
  return cfg.stingers.cues[CHARTER_CUES[cue]].braaam
}
